import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';

const CardGenerator = () => {
  // States for card text and style options.
  const [cardText, setCardText] = useState("");
  const [themeColor, setThemeColor] = useState("blue");
  const [fontFamily, setFontFamily] = useState("sans");
  const [textSize, setTextSize] = useState("text-lg");
  const [design, setDesign] = useState("simple");

  // Reference to the card preview element
  const cardRef = useRef(null);

  // Define design options.
  // For the gradient design, we apply inline gradient styling.
  const cardDesignClasses = {
    simple: "p-6 rounded",
    shadow: "p-6 rounded shadow-lg",
    bordered: "p-6 rounded border",
    gradient: "p-6 rounded", // gradient styling will be applied inline
  };

  // Inline gradient style (using standard hex colors)
  const gradientStyle = design === "gradient" ? {
    background: "linear-gradient(to right, #3B82F6, #8B5CF6)",
    color: "#ffffff"
  } : {};

  // Define a mapping from theme names to hex color values.
  const themeColorsHex = {
    blue: { bg: "#DBEAFE", text: "#1E3A8A" },
    red: { bg: "#FECACA", text: "#B91C1C" },
    green: { bg: "#DCFCE7", text: "#047857" },
    yellow: { bg: "#FEF9C3", text: "#CA8A04" },
    purple: { bg: "#E9D5FF", text: "#6B21A8" },
  };

  // Build the inline style for the card.
  const cardInlineStyle = design === "gradient"
    ? { 
        ...gradientStyle, 
        fontFamily: fontFamily === "sans" ? "sans-serif" : fontFamily === "serif" ? "serif" : "monospace"
      }
    : { 
        backgroundColor: themeColorsHex[themeColor].bg, 
        color: themeColorsHex[themeColor].text, 
        fontFamily: fontFamily === "sans" ? "sans-serif" : fontFamily === "serif" ? "serif" : "monospace"
      };

  // Handler to save card as image
  const handleSaveAsImage = () => {
    if (cardRef.current) {
      html2canvas(cardRef.current).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        // Create a link element to trigger download
        const link = document.createElement('a');
        link.href = imgData;
        link.download = 'card.png';
        link.click();
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Automated Card Generator</h1>
      
      {/* Form for card customization */}
      <div className="mb-6 border p-4 rounded shadow">
        <div className="mb-4">
          <label className="block font-medium mb-1">Card Text:</label>
          <textarea
            value={cardText}
            onChange={(e) => setCardText(e.target.value)}
            placeholder="Enter your card message here..."
            rows="3"
            className="border rounded w-full p-2"
          />
        </div>
        
        <div className="mb-4">
          <label className="block font-medium mb-1">Theme Color:</label>
          <select
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            className="border rounded w-full p-2"
          >
            <option value="blue">Blue</option>
            <option value="red">Red</option>
            <option value="green">Green</option>
            <option value="yellow">Yellow</option>
            <option value="purple">Purple</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block font-medium mb-1">Font:</label>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            className="border rounded w-full p-2"
          >
            <option value="sans">Sans (default)</option>
            <option value="serif">Serif</option>
            <option value="mono">Monospace</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block font-medium mb-1">Text Size:</label>
          <select
            value={textSize}
            onChange={(e) => setTextSize(e.target.value)}
            className="border rounded w-full p-2"
          >
            <option value="text-sm">Small</option>
            <option value="text-base">Base</option>
            <option value="text-lg">Large</option>
            <option value="text-xl">Extra Large</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block font-medium mb-1">Design:</label>
          <select
            value={design}
            onChange={(e) => setDesign(e.target.value)}
            className="border rounded w-full p-2"
          >
            <option value="simple">Simple</option>
            <option value="shadow">Shadow</option>
            <option value="bordered">Bordered</option>
            <option value="gradient">Gradient</option>
          </select>
        </div>
      </div>

      {/* Card Preview */}
      <h2 className="text-2xl font-semibold mb-4">Preview</h2>
      <div
        ref={cardRef}
        className={cardDesignClasses[design]}
        style={cardInlineStyle}
      >
        <p className={textSize}>
          {cardText || "Your card text will appear here..."}
        </p>
      </div>

      {/* Save as Image Button */}
      <button 
        onClick={handleSaveAsImage}
        className="mt-4 bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
      >
        Save Card as Image
      </button>
    </div>
  );
};

export default CardGenerator;
